import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const useComplianceFunctions = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [showNewForm, setShowNewForm] = useState(false);
  const [newReport, setNewReport] = useState({ name: '', category: 'Regulatory', dueDate: '', period: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(null);

  const fetchReports = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus !== 'All') params.status = filterStatus;
      if (filterCategory !== 'All') params.category = filterCategory;
      if (search) params.search = search;
      const resp = await axios.get(`${BASE_URL}/compliance-reports`, { params, withCredentials: true });
      if (resp.data?.data?.length > 0) setReports(resp.data.data);
    } catch {
      /* show empty state on error */
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterCategory, search]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const categories = ['All', ...Array.from(new Set(reports.map(r => r.category)))];
  const statuses = ['All', 'submitted', 'review', 'draft', 'overdue'];

  const filtered = reports.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q || r.name?.toLowerCase().includes(q) || r.reportId?.toLowerCase().includes(q);
    const matchCat = filterCategory === 'All' || r.category === filterCategory;
    const matchStatus = filterStatus === 'All' || r.status === filterStatus;
    return matchSearch && matchCat && matchStatus;
  });

  const submitted = reports.filter(r => r.status === 'submitted').length;
  const pending = reports.filter(r => r.status === 'draft' || r.status === 'review').length;
  const dueThisMonth = reports.filter(r => {
    if (!r.dueDate) return false;
    const d = new Date(r.dueDate);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  const handleCreate = async () => {
    if (!newReport.name.trim() || !newReport.category) return;
    setSubmitting(true);
    try {
      const resp = await axios.post(`${BASE_URL}/compliance-reports`, newReport, { withCredentials: true });
      if (resp.data?.data) setReports(prev => [resp.data.data, ...prev]);
    } catch {
      const fallbackId = `CR-${new Date().getFullYear()}-${String(reports.length + 1).padStart(3, '0')}`;
      setReports(prev => [{ ...newReport, _id: Date.now().toString(), reportId: fallbackId, status: 'draft', submittedDate: null }, ...prev]);
    } finally {
      setSubmitting(false);
      setShowNewForm(false);
      setNewReport({ name: '', category: 'Regulatory', dueDate: '', period: '', notes: '' });
    }
  };

  const handleGenerate = async report => {
    setGenerating(report._id);
    try {
      const resp = await axios.post(
        `${BASE_URL}/compliance-reports/${report._id}/generate`,
        {},
        {
          withCredentials: true,
          responseType: 'blob',
        },
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/plain' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.reportId || 'report'}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      setReports(prev => prev.map(r => (r._id === report._id ? { ...r, status: r.status === 'draft' ? 'review' : r.status, generatedAt: new Date() } : r)));
    } catch {
      alert('Report generation failed. Please try again.');
    } finally {
      setGenerating(null);
    }
  };

  const handleSubmit = async report => {
    try {
      const resp = await axios.patch(`${BASE_URL}/compliance-reports/${report._id}`, { status: 'submitted', submittedDate: new Date() }, { withCredentials: true });
      if (resp.data?.data) setReports(prev => prev.map(r => (r._id === report._id ? resp.data.data : r)));
    } catch {
      setReports(prev => prev.map(r => (r._id === report._id ? { ...r, status: 'submitted', submittedDate: new Date().toISOString() } : r)));
    }
  };

  return {
    reports,
    setReports,
    loading,
    setLoading,
    search,
    setSearch,
    filterCategory,
    setFilterCategory,
    filterStatus,
    setFilterStatus,
    showNewForm,
    setShowNewForm,
    newReport,
    setNewReport,
    submitting,
    setSubmitting,
    generating,
    setGenerating,
    fetchReports,
    categories,
    statuses,
    filtered,
    submitted,
    pending,
    dueThisMonth,
    handleCreate,
    handleGenerate,
    handleSubmit,
  };
};

export default useComplianceFunctions;
